import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getAdminOverview,
  getAdminUsers,
  getAdminActivity,
  updateUserSubscription,
  sendAnnouncement,
  AdminOverview,
  AdminUser,
  ActivityDay,
} from '../../services/adminService';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Legend,
} from 'recharts';
import { collection, doc, addDoc, onSnapshot, query, orderBy, where, updateDoc, setDoc, serverTimestamp, Unsubscribe, getDocs, deleteDoc } from 'firebase/firestore';
import { db, auth } from '../../firebaseConfig';

type Tab = 'overview' | 'users' | 'analytics' | 'support' | 'announce';
type SortKey = keyof AdminUser;
type SortDir = 'asc' | 'desc';

const ADMIN_EMAILS = ['chris@splat.sh', 'christos@splat.sh'];

/* ────────────────────────────────────────────
   Relative time helper
   ──────────────────────────────────────────── */

const relativeTime = (date: any): string => {
  if (!date) return '';
  let ms: number;
  if (date?.toDate) {
    ms = Date.now() - date.toDate().getTime();
  } else if (date?.seconds) {
    ms = Date.now() - date.seconds * 1000;
  } else {
    ms = Date.now() - new Date(date).getTime();
  }
  if (ms < 0) ms = 0;
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'Yesterday';
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
};

/* ────────────────────────────────────────────
   Main Admin Screen
   ──────────────────────────────────────────── */

const Admin: React.FC = () => {
  const { isDark } = useTheme();
  const [tab, setTab] = useState<Tab>('overview');
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [activity, setActivity] = useState<ActivityDay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Users tab state
  const [search, setSearch] = useState('');
  const [filterTier, setFilterTier] = useState<'all' | 'pro' | 'free'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('accountCreatedAt');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);

  // Subscription modal state
  const [modalTier, setModalTier] = useState('free');
  const [modalExpiry, setModalExpiry] = useState('');
  const [modalTrial, setModalTrial] = useState(false);
  const [modalSaving, setModalSaving] = useState(false);

  // Support tab state
  const [supportChats, setSupportChats] = useState<any[]>([]);
  const [selectedChat, setSelectedChat] = useState<any | null>(null);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [messageText, setMessageText] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Announcements
  const [showAnnounceForm, setShowAnnounceForm] = useState(false);
  const [announceTitle, setAnnounceTitle] = useState('');
  const [announceMessage, setAnnounceMessage] = useState('');
  const [announceSending, setAnnounceSending] = useState(false);
  const [announceResult, setAnnounceResult] = useState<{ sent: number; failed: number; total: number } | null>(null);
  const [announceMode, setAnnounceMode] = useState<'test' | 'prod'>('test');
  const [announcements, setAnnouncements] = useState<any[]>([]);
  const [showProdConfirm, setShowProdConfirm] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ov, us, ac] = await Promise.all([
        getAdminOverview(),
        getAdminUsers(),
        getAdminActivity(60),
      ]);
      setOverview(ov);
      setUsers(us);
      setActivity(ac);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Failed to load admin data';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Subscribe to support chats when tab is 'support'
  useEffect(() => {
    if (tab !== 'support') return;

    const currentUid = auth.currentUser?.uid;
    if (!currentUid) return;

    const q = query(
      collection(db, 'supportChats'),
      where('participants', 'array-contains', currentUid),
      orderBy('lastMessageAt', 'desc')
    );
    const unsub: Unsubscribe = onSnapshot(q, (snapshot) => {
      const chats = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setSupportChats(chats);
    });

    return () => unsub();
  }, [tab]);

  // Subscribe to messages when selectedChat changes
  useEffect(() => {
    if (!selectedChat) {
      setChatMessages([]);
      return;
    }

    const q = query(
      collection(db, 'supportChats', selectedChat.id, 'messages'),
      orderBy('createdAt', 'asc')
    );
    const unsub: Unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setChatMessages(msgs);
    });

    // Reset unread count for admin
    if (selectedChat.unreadByAdmin > 0) {
      updateDoc(doc(db, 'supportChats', selectedChat.id), {
        unreadByAdmin: 0,
      }).catch(() => {});
    }

    return () => unsub();
  }, [selectedChat?.id]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Auto-resize textarea
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 200) + 'px';
  }, [messageText]);

  const sendMessage = async () => {
    if (!messageText.trim() || !selectedChat || sendingMessage) return;
    setSendingMessage(true);
    const text = messageText.trim();
    setMessageText('');
    try {
      await addDoc(collection(db, 'supportChats', selectedChat.id, 'messages'), {
        senderId: auth.currentUser?.uid || '',
        senderName: auth.currentUser?.displayName || 'Admin',
        senderEmail: auth.currentUser?.email || '',
        text,
        createdAt: serverTimestamp(),
      });
      // Update last message preview (unread count handled by Cloud Function)
      await updateDoc(doc(db, 'supportChats', selectedChat.id), {
        lastMessage: text,
        lastMessageAt: serverTimestamp(),
      });
    } catch (e) {
      console.error('Failed to send message:', e);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleSupportKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Load announcements when announce tab is active
  const fetchAnnouncements = useCallback(async () => {
    try {
      const q = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setAnnouncements(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (e) {
      console.error('Failed to load announcements:', e);
    }
  }, []);

  useEffect(() => {
    if (tab === 'announce') {
      fetchAnnouncements();
    }
  }, [tab, fetchAnnouncements]);

  const handleSendAnnouncement = async (testOnly: boolean) => {
    if (!announceTitle.trim() || !announceMessage.trim() || announceSending) return;
    setAnnounceSending(true);
    setAnnounceResult(null);
    try {
      const result = await sendAnnouncement({
        title: announceTitle.trim(),
        message: announceMessage.trim(),
        testOnly,
      });
      setAnnounceResult(result);
      setAnnounceTitle('');
      setAnnounceMessage('');
      await fetchAnnouncements();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to send announcement');
    } finally {
      setAnnounceSending(false);
      setShowProdConfirm(false);
    }
  };

  const handleAnnouncementDelete = async (annId: string) => {
    try {
      await deleteDoc(doc(db, 'announcements', annId));
      setAnnouncements(prev => prev.filter(a => a.id !== annId));
    } catch (e) {
      console.error('Failed to delete announcement:', e);
    }
  };

  // Open subscription modal
  const openSubModal = (user: AdminUser) => {
    setSelectedUser(user);
    setModalTier(user.subscriptionTier);
    setModalExpiry(user.subscriptionExpirationDate ? user.subscriptionExpirationDate.split('T')[0] : '');
    setModalTrial(user.subscriptionIsTrialPeriod);
  };

  const saveSubscription = async () => {
    if (!selectedUser) return;
    setModalSaving(true);
    try {
      await updateUserSubscription({
        uid: selectedUser.uid,
        subscriptionTier: modalTier,
        subscriptionExpirationDate: modalTier === 'pro' && modalExpiry ? new Date(modalExpiry).toISOString() : undefined,
        subscriptionIsTrialPeriod: modalTier === 'pro' ? modalTrial : false,
      });
      // Update local state
      setUsers(prev => prev.map(u =>
        u.uid === selectedUser.uid
          ? { ...u, subscriptionTier: modalTier, subscriptionExpirationDate: modalTier === 'pro' && modalExpiry ? new Date(modalExpiry).toISOString() : null, subscriptionIsTrialPeriod: modalTier === 'pro' ? modalTrial : false }
          : u
      ));
      setSelectedUser(null);
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Failed to update subscription');
    } finally {
      setModalSaving(false);
    }
  };

  // Filtered + sorted users
  const filteredUsers = useMemo(() => {
    let list = users;
    if (filterTier !== 'all') list = list.filter(u => u.subscriptionTier === filterTier);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        u.displayName.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q)
      );
    }
    list = [...list].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
      const as = String(av || '');
      const bs = String(bv || '');
      return sortDir === 'asc' ? as.localeCompare(bs) : bs.localeCompare(as);
    });
    return list;
  }, [users, filterTier, search, sortKey, sortDir]);

  // Analytics computed from users
  const analytics = useMemo(() => {
    if (!users.length) return null;
    const total = users.length;

    // Feature adoption: % of users who have at least 1 of each
    const features = [
      { name: 'Tasks', key: 'taskCount' as const },
      { name: 'Habits', key: 'habitCount' as const },
      { name: 'Lists', key: 'listCount' as const },
      { name: 'Notes', key: 'noteCount' as const },
      { name: 'Affirmations', key: 'affirmationCount' as const },
      { name: 'Moods', key: 'feelingCount' as const },
      { name: 'Gantt', key: 'ganttCount' as const },
      { name: 'Budget', key: 'budgetCategoryCount' as const },
      { name: 'Connections', key: 'connectionCount' as const },
    ];
    const adoption = features.map(f => ({
      name: f.name,
      pct: Math.round((users.filter(u => u[f.key] > 0).length / total) * 100),
      count: users.filter(u => u[f.key] > 0).length,
    })).sort((a, b) => b.pct - a.pct);

    // Power users (top 10 by total activity)
    const withActivity = users.map(u => ({
      ...u,
      totalActivity: u.taskCount + u.habitCount + u.listCount + u.noteCount + u.affirmationCount + u.feelingCount + u.ganttCount + u.budgetCategoryCount,
    })).sort((a, b) => b.totalActivity - a.totalActivity);
    const powerUsers = withActivity.slice(0, 10);

    // Churn: users with no lastActiveAt or lastActiveAt > 14 days ago
    const fourteenDaysAgo = new Date();
    fourteenDaysAgo.setDate(fourteenDaysAgo.getDate() - 14);
    const fourteenStr = fourteenDaysAgo.toISOString();
    const churned = users.filter(u => !u.lastActiveAt || u.lastActiveAt < fourteenStr);
    const active = users.filter(u => u.lastActiveAt && u.lastActiveAt >= fourteenStr);

    // Pro conversion
    const proUsers = users.filter(u => u.subscriptionTier === 'pro');

    return { adoption, powerUsers, churned, active, proUsers, total };
  }, [users]);

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  // Styling helpers
  const card = `rounded-2xl p-6 ${isDark ? 'bg-neutral-900 border border-neutral-800' : 'bg-white border border-gray-200 shadow-sm'}`;
  const textPrimary = isDark ? 'text-white' : 'text-black';
  const textSecondary = isDark ? 'text-neutral-400' : 'text-gray-500';
  const textMuted = isDark ? 'text-neutral-500' : 'text-gray-400';

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className="text-center">
          <div className={`animate-spin rounded-full h-10 w-10 border-b-2 ${isDark ? 'border-white' : 'border-black'} mx-auto mb-3`} />
          <p className={textSecondary}>Loading admin data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <div className={`${card} max-w-md text-center`}>
          <p className="text-red-500 mb-4">{error}</p>
          <button onClick={fetchData} className="px-4 py-2 bg-purple-600 text-white rounded-xl text-sm font-medium">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const formatDate = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const daysAgo = (iso: string | null) => {
    if (!iso) return null;
    const diff = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return `${diff}d ago`;
  };

  const isMyMessage = (msg: any) => {
    return ADMIN_EMAILS.includes(msg.senderEmail);
  };

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'} p-4 lg:p-8`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${textPrimary}`}>Admin portal</h1>
          <p className={`text-sm ${textSecondary}`}>taskDojo management dashboard</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowAnnounceForm(f => !f); setAnnounceResult(null); }}
            className="px-3 py-2 rounded-xl text-sm font-medium bg-purple-500/20 text-purple-500 hover:bg-purple-500/30"
          >
            Announce
          </button>
          <button
            onClick={fetchData}
            className={`px-3 py-2 rounded-xl text-sm font-medium ${isDark ? 'bg-neutral-800 text-neutral-300 hover:bg-neutral-700' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Announce form */}
      {showAnnounceForm && (
        <div className={`mb-6 rounded-2xl p-5 ${isDark ? 'bg-neutral-900 border border-neutral-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
          <h3 className={`text-sm font-semibold mb-3 ${textSecondary}`}>Send announcement to all users</h3>
          <input
            type="text"
            placeholder="Title"
            value={announceTitle}
            onChange={e => setAnnounceTitle(e.target.value)}
            className={`w-full px-4 py-2 rounded-xl text-sm mb-3 outline-none ${isDark ? 'bg-neutral-800 text-white border border-neutral-700 placeholder-neutral-500' : 'bg-gray-50 text-black border border-gray-200 placeholder-gray-400'}`}
          />
          <textarea
            placeholder="Message..."
            value={announceMessage}
            onChange={e => setAnnounceMessage(e.target.value)}
            rows={3}
            className={`w-full px-4 py-2 rounded-xl text-sm mb-3 outline-none resize-none ${isDark ? 'bg-neutral-800 text-white border border-neutral-700 placeholder-neutral-500' : 'bg-gray-50 text-black border border-gray-200 placeholder-gray-400'}`}
          />
          <div className="flex items-center gap-3">
            <button
              onClick={async () => {
                if (!announceTitle.trim() || !announceMessage.trim() || announceSending) return;
                setAnnounceSending(true);
                setAnnounceResult(null);
                try {
                  const result = await sendAnnouncement({ title: announceTitle.trim(), message: announceMessage.trim() });
                  setAnnounceResult(result);
                  setAnnounceTitle('');
                  setAnnounceMessage('');
                } catch (e: unknown) {
                  alert(e instanceof Error ? e.message : 'Failed to send announcement');
                } finally {
                  setAnnounceSending(false);
                }
              }}
              disabled={announceSending || !announceTitle.trim() || !announceMessage.trim()}
              className="px-4 py-2 rounded-xl bg-purple-600 text-white text-sm font-semibold disabled:opacity-50"
            >
              {announceSending ? 'Sending...' : 'Send to all users'}
            </button>
            <button
              onClick={() => { setShowAnnounceForm(false); setAnnounceResult(null); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium ${isDark ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'}`}
            >
              Cancel
            </button>
            {announceResult && (
              <span className={`text-sm ${textSecondary}`}>
                Sent: {announceResult.sent} · Failed: {announceResult.failed} · Total: {announceResult.total}
              </span>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className={`flex gap-1 p-1 rounded-xl mb-6 w-fit ${isDark ? 'bg-neutral-900' : 'bg-gray-100'}`}>
        {(['overview', 'users', 'analytics', 'support', 'announce'] as Tab[]).map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`px-5 py-2 rounded-lg text-sm font-medium capitalize transition-all ${
              tab === t
                ? isDark ? 'bg-white text-black' : 'bg-black text-white'
                : isDark ? 'text-neutral-400 hover:text-white' : 'text-gray-500 hover:text-black'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ═══════════ OVERVIEW TAB ═══════════ */}
      {tab === 'overview' && overview && (
        <div className="space-y-6">
          {/* KPI Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard isDark={isDark} label="Total users" value={overview.totalUsers} />
            <KPICard isDark={isDark} label="Pro users" value={overview.proUsers} accent="purple" />
            <KPICard isDark={isDark} label="Free users" value={overview.freeUsers} />
            <KPICard isDark={isDark} label="New (7d)" value={overview.newUsers7d} accent="green" />
            <KPICard isDark={isDark} label="New (30d)" value={overview.newUsers30d} accent="green" />
            <KPICard isDark={isDark} label="Active (7d)" value={overview.activeUsers7d} accent="blue" />
            <KPICard isDark={isDark} label="Active (30d)" value={overview.activeUsers30d} accent="blue" />
            <KPICard isDark={isDark} label="Pro rate" value={`${overview.totalUsers ? Math.round((overview.proUsers / overview.totalUsers) * 100) : 0}%`} accent="purple" />
          </div>

          {/* Feature totals */}
          <div className={card}>
            <h3 className={`text-sm font-semibold mb-4 ${textSecondary}`}>Feature totals</h3>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { label: 'Tasks', value: overview.totalTasks },
                { label: 'Habits', value: overview.totalHabits },
                { label: 'Lists', value: overview.totalLists },
                { label: 'Notes', value: overview.totalNotes },
                { label: 'Affirmations', value: overview.totalAffirmations },
                { label: 'Gantt Charts', value: overview.totalGanttCharts },
                { label: 'Budget Categories', value: overview.totalBudgetCategories },
                { label: 'Budget Entries', value: overview.totalBudgetEntries },
              ].map(f => (
                <div key={f.label}>
                  <p className={`text-2xl font-bold ${textPrimary}`}>{f.value.toLocaleString()}</p>
                  <p className={`text-xs ${textMuted}`}>{f.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* User growth Chart */}
          {overview.growth.length > 0 && (
            <div className={card}>
              <h3 className={`text-sm font-semibold mb-4 ${textSecondary}`}>User growth</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={overview.growth}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                  <XAxis dataKey="date" tick={{ fill: isDark ? '#888' : '#666', fontSize: 11 }} tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis tick={{ fill: isDark ? '#888' : '#666', fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`, borderRadius: 12, fontSize: 13 }} />
                  <Area type="monotone" dataKey="totalUsers" stroke="#8B5CF6" fill="#8B5CF680" name="Total users" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Daily Active Users */}
          {activity.length > 0 && (
            <div className={card}>
              <h3 className={`text-sm font-semibold mb-4 ${textSecondary}`}>Daily active users (60 days)</h3>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={activity}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                  <XAxis dataKey="date" tick={{ fill: isDark ? '#888' : '#666', fontSize: 11 }} tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis tick={{ fill: isDark ? '#888' : '#666', fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`, borderRadius: 12, fontSize: 13 }} />
                  <Area type="monotone" dataKey="activeUsers" stroke="#3B82F6" fill="#3B82F680" name="Active users" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Feature usage Breakdown */}
          {activity.length > 0 && (
            <div className={card}>
              <h3 className={`text-sm font-semibold mb-4 ${textSecondary}`}>Feature usage (60 days)</h3>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={activity.filter((_, i) => i % 3 === 0)}>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? '#333' : '#e5e7eb'} />
                  <XAxis dataKey="date" tick={{ fill: isDark ? '#888' : '#666', fontSize: 10 }} tickFormatter={(v: string) => v.slice(5)} />
                  <YAxis tick={{ fill: isDark ? '#888' : '#666', fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: isDark ? '#1a1a1a' : '#fff', border: `1px solid ${isDark ? '#333' : '#e5e7eb'}`, borderRadius: 12, fontSize: 13 }} />
                  <Legend />
                  <Bar dataKey="tasks" stackId="a" fill="#8B5CF6" name="Tasks" />
                  <Bar dataKey="habits" stackId="a" fill="#3B82F6" name="Habits" />
                  <Bar dataKey="lists" stackId="a" fill="#10B981" name="Lists" />
                  <Bar dataKey="notes" stackId="a" fill="#F59E0B" name="Notes" />
                  <Bar dataKey="affirmations" stackId="a" fill="#EC4899" name="Affirmations" />
                  <Bar dataKey="budget" stackId="a" fill="#6366F1" name="Budget" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ═══════════ USERS TAB ═══════════ */}
      {tab === 'users' && (
        <div className="space-y-4">
          {/* Search + Filter */}
          <div className="flex flex-wrap gap-3 items-center">
            <input
              type="text"
              placeholder="Search name or email..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className={`px-4 py-2 rounded-xl text-sm w-64 outline-none ${isDark ? 'bg-neutral-900 text-white border border-neutral-700 placeholder-neutral-500' : 'bg-white text-black border border-gray-200 placeholder-gray-400'}`}
            />
            <div className="flex gap-1">
              {(['all', 'pro', 'free'] as const).map(f => (
                <button
                  key={f}
                  onClick={() => setFilterTier(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize ${
                    filterTier === f
                      ? isDark ? 'bg-white text-black' : 'bg-black text-white'
                      : isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-500'
                  }`}
                >
                  {f} {f !== 'all' && `(${users.filter(u => f === 'pro' ? u.subscriptionTier === 'pro' : u.subscriptionTier !== 'pro').length})`}
                </button>
              ))}
            </div>
            <span className={`text-xs ${textMuted}`}>{filteredUsers.length} users</span>
          </div>

          {/* Table */}
          <div className={`${card} overflow-x-auto !p-0`}>
            <table className="w-full text-sm">
              <thead>
                <tr className={isDark ? 'border-b border-neutral-800' : 'border-b border-gray-100'}>
                  {[
                    { key: 'displayName', label: 'Name' },
                    { key: 'email', label: 'Email' },
                    { key: 'subscriptionTier', label: 'Tier' },
                    { key: 'taskCount', label: 'Tasks' },
                    { key: 'habitCount', label: 'Habits' },
                    { key: 'listCount', label: 'Lists' },
                    { key: 'noteCount', label: 'Notes' },
                    { key: 'connectionCount', label: 'Conns' },
                    { key: 'lastActiveAt', label: 'Last Active' },
                    { key: 'accountCreatedAt', label: 'Joined' },
                    { key: 'deviceType', label: 'Device' },
                    { key: 'appVersion', label: 'Version' },
                  ].map(col => (
                    <th
                      key={col.key}
                      onClick={() => handleSort(col.key as SortKey)}
                      className={`px-4 py-3 text-left font-medium cursor-pointer select-none whitespace-nowrap ${textSecondary} hover:${textPrimary}`}
                    >
                      {col.label}
                      {sortKey === col.key && (
                        <span className="ml-1">{sortDir === 'asc' ? '\u2191' : '\u2193'}</span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => (
                  <tr
                    key={user.uid}
                    className={`${isDark ? 'border-b border-neutral-800/50 hover:bg-neutral-800/50' : 'border-b border-gray-50 hover:bg-gray-50'} cursor-pointer`}
                    onClick={() => openSubModal(user)}
                  >
                    <td className={`px-4 py-3 font-medium ${textPrimary}`}>{user.displayName || '—'}</td>
                    <td className={`px-4 py-3 ${textSecondary}`}>{user.email}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
                        user.subscriptionTier === 'pro'
                          ? 'bg-purple-500/20 text-purple-400'
                          : isDark ? 'bg-neutral-700 text-neutral-300' : 'bg-gray-100 text-gray-500'
                      }`}>
                        {user.subscriptionTier.toUpperCase()}
                      </span>
                    </td>
                    <td className={`px-4 py-3 ${textPrimary}`}>{user.taskCount}</td>
                    <td className={`px-4 py-3 ${textPrimary}`}>{user.habitCount}</td>
                    <td className={`px-4 py-3 ${textPrimary}`}>{user.listCount}</td>
                    <td className={`px-4 py-3 ${textPrimary}`}>{user.noteCount}</td>
                    <td className={`px-4 py-3 ${textPrimary}`}>{user.connectionCount}</td>
                    <td className={`px-4 py-3 whitespace-nowrap ${
                      user.lastActiveAt && daysAgo(user.lastActiveAt) === 'Today' ? 'text-green-500' :
                      !user.lastActiveAt ? 'text-red-400' : textSecondary
                    }`}>
                      {daysAgo(user.lastActiveAt) || 'Never'}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap ${textMuted}`}>{formatDate(user.accountCreatedAt)}</td>
                    <td className="px-4 py-3">
                      {user.deviceType ? (
                        <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
                          user.deviceType === 'ios'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {user.deviceType === 'ios' ? 'iOS' : 'Android'}
                        </span>
                      ) : <span className={textMuted}>—</span>}
                    </td>
                    <td className={`px-4 py-3 whitespace-nowrap ${textSecondary}`}>{user.appVersion || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ═══════════ ANALYTICS TAB ═══════════ */}
      {tab === 'analytics' && analytics && (
        <div className="space-y-6">
          {/* Summary KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard isDark={isDark} label="Total users" value={analytics.total} />
            <KPICard isDark={isDark} label="Active (14d)" value={analytics.active.length} accent="green" />
            <KPICard isDark={isDark} label="Inactive (14d+)" value={analytics.churned.length} accent="red" />
            <KPICard isDark={isDark} label="Pro users" value={analytics.proUsers.length} accent="purple" />
          </div>

          {/* Feature adoption */}
          <div className={card}>
            <h3 className={`text-sm font-semibold mb-4 ${textSecondary}`}>Feature adoption</h3>
            <div className="space-y-3">
              {analytics.adoption.map(f => (
                <div key={f.name} className="flex items-center gap-3">
                  <span className={`text-sm w-24 ${textPrimary}`}>{f.name}</span>
                  <div className={`flex-1 h-6 rounded-lg overflow-hidden ${isDark ? 'bg-neutral-800' : 'bg-gray-100'}`}>
                    <div
                      className="h-full rounded-lg bg-gradient-to-r from-purple-600 to-purple-400 flex items-center justify-end pr-2"
                      style={{ width: `${Math.max(f.pct, 4)}%` }}
                    >
                      {f.pct > 15 && <span className="text-[10px] font-bold text-white">{f.pct}%</span>}
                    </div>
                  </div>
                  {f.pct <= 15 && <span className={`text-xs font-medium ${textSecondary}`}>{f.pct}%</span>}
                  <span className={`text-xs ${textMuted} w-16 text-right`}>{f.count}/{analytics.total}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Power Users */}
          <div className={card}>
            <h3 className={`text-sm font-semibold mb-4 ${textSecondary}`}>Top 10 power users</h3>
            <div className="space-y-2">
              {analytics.powerUsers.map((u, i) => (
                <div
                  key={u.uid}
                  className={`flex items-center gap-3 px-3 py-2 rounded-xl ${isDark ? 'hover:bg-neutral-800' : 'hover:bg-gray-50'} cursor-pointer`}
                  onClick={() => openSubModal(u)}
                >
                  <span className={`text-xs font-bold w-6 ${textMuted}`}>#{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-medium truncate ${textPrimary}`}>{u.displayName || u.email}</p>
                    <p className={`text-xs truncate ${textMuted}`}>{u.email}</p>
                  </div>
                  <span className={`px-2 py-0.5 rounded-md text-xs font-semibold ${
                    u.subscriptionTier === 'pro'
                      ? 'bg-purple-500/20 text-purple-400'
                      : isDark ? 'bg-neutral-700 text-neutral-300' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {u.subscriptionTier.toUpperCase()}
                  </span>
                  <span className={`text-sm font-semibold ${textPrimary}`}>{u.totalActivity}</span>
                  <span className={`text-xs ${textMuted}`}>items</span>
                </div>
              ))}
            </div>
          </div>

          {/* Churn / Inactive Users */}
          <div className={card}>
            <h3 className={`text-sm font-semibold mb-1 ${textSecondary}`}>Inactive users (14+ days)</h3>
            <p className={`text-xs mb-4 ${textMuted}`}>{analytics.churned.length} users have not been active in the last 14 days</p>
            <div className="space-y-1 max-h-80 overflow-y-auto">
              {analytics.churned
                .sort((a, b) => {
                  if (!a.lastActiveAt) return 1;
                  if (!b.lastActiveAt) return -1;
                  return a.lastActiveAt.localeCompare(b.lastActiveAt);
                })
                .map(u => (
                  <div
                    key={u.uid}
                    className={`flex items-center gap-3 px-3 py-2 rounded-xl ${isDark ? 'hover:bg-neutral-800' : 'hover:bg-gray-50'} cursor-pointer`}
                    onClick={() => openSubModal(u)}
                  >
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${textPrimary}`}>{u.displayName || u.email}</p>
                    </div>
                    <span className={`text-xs ${textMuted}`}>{u.email}</span>
                    <span className={`text-xs whitespace-nowrap ${!u.lastActiveAt ? 'text-red-400' : 'text-orange-400'}`}>
                      {daysAgo(u.lastActiveAt) || 'Never active'}
                    </span>
                  </div>
                ))}
            </div>
          </div>

          {/* Pro conversion */}
          <div className={card}>
            <h3 className={`text-sm font-semibold mb-4 ${textSecondary}`}>Pro conversion</h3>
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className={`text-3xl font-bold ${textPrimary}`}>{analytics.proUsers.length}</p>
                <p className={`text-xs ${textMuted}`}>Pro subscribers</p>
              </div>
              <div>
                <p className={`text-3xl font-bold ${textPrimary}`}>{analytics.total - analytics.proUsers.length}</p>
                <p className={`text-xs ${textMuted}`}>Free users</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-purple-500">
                  {analytics.total ? Math.round((analytics.proUsers.length / analytics.total) * 100) : 0}%
                </p>
                <p className={`text-xs ${textMuted}`}>Conversion rate</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ SUPPORT TAB ═══════════ */}
      {tab === 'support' && (
        <div className={`${card} !p-0 overflow-hidden flex`} style={{ height: 'calc(100vh - 220px)' }}>
          {/* Left panel: chat list */}
          <div className={`w-80 flex-shrink-0 border-r ${isDark ? 'border-neutral-800' : 'border-gray-200'} flex flex-col`}>
            <div className={`px-4 py-3 border-b ${isDark ? 'border-neutral-800' : 'border-gray-200'}`}>
              <h3 className={`text-sm font-semibold ${textPrimary}`}>Support chats</h3>
              <p className={`text-xs ${textMuted}`}>{supportChats.length} conversation{supportChats.length !== 1 ? 's' : ''}</p>
            </div>
            <div className="flex-1 overflow-y-auto">
              {supportChats.length === 0 ? (
                <div className="flex items-center justify-center h-full">
                  <p className={`text-sm ${textMuted}`}>No support chats yet</p>
                </div>
              ) : (
                supportChats.map((chat) => (
                  <div
                    key={chat.id}
                    onClick={() => setSelectedChat(chat)}
                    className={`px-4 py-3 cursor-pointer border-b transition-colors ${
                      selectedChat?.id === chat.id
                        ? isDark ? 'bg-neutral-800 border-neutral-700' : 'bg-purple-50 border-gray-200'
                        : isDark ? 'border-neutral-800/50 hover:bg-neutral-800/50' : 'border-gray-100 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <p className={`text-sm font-medium truncate ${textPrimary}`}>{chat.userName || 'Unknown'}</p>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {(chat.unreadByAdmin || 0) > 0 && (
                          <span className="w-5 h-5 rounded-full bg-purple-600 text-white text-[10px] font-bold flex items-center justify-center">
                            {chat.unreadByAdmin}
                          </span>
                        )}
                        <span className={`text-[10px] ${textMuted}`}>{relativeTime(chat.lastMessageAt)}</span>
                      </div>
                    </div>
                    <p className={`text-xs truncate ${textMuted}`}>{chat.userEmail || ''}</p>
                    {chat.lastMessage && (
                      <p className={`text-xs truncate mt-1 ${textSecondary}`}>{chat.lastMessage}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right panel: messages */}
          <div className="flex-1 flex flex-col min-w-0">
            {!selectedChat ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <svg className={`w-12 h-12 mx-auto mb-3 ${textMuted}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                  </svg>
                  <p className={`text-sm ${textMuted}`}>Select a conversation</p>
                </div>
              </div>
            ) : (
              <>
                {/* Chat header */}
                <div className={`px-5 py-3 border-b flex-shrink-0 ${isDark ? 'border-neutral-800' : 'border-gray-200'}`}>
                  <p className={`text-sm font-semibold ${textPrimary}`}>{selectedChat.userName || 'Unknown'}</p>
                  <p className={`text-xs ${textMuted}`}>{selectedChat.userEmail || ''}</p>
                </div>

                {/* Messages area */}
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {chatMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className={`text-sm ${textMuted}`}>No messages yet</p>
                    </div>
                  ) : (
                    chatMessages.map((msg) => {
                      const mine = isMyMessage(msg);
                      return (
                        <div key={msg.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                          <div
                            className={
                              mine
                                ? 'bg-purple-600 text-white rounded-2xl px-4 py-2.5 max-w-[75%]'
                                : `${isDark ? 'bg-neutral-800' : 'bg-gray-100'} rounded-2xl px-4 py-2.5 max-w-[75%]`
                            }
                          >
                            {!mine && (
                              <p className={`text-[10px] font-medium mb-1 ${isDark ? 'text-neutral-400' : 'text-gray-500'}`}>
                                {msg.senderName || 'User'}
                              </p>
                            )}
                            <p className={`text-sm whitespace-pre-wrap ${mine ? 'text-white' : textPrimary}`}>{msg.text}</p>
                          </div>
                          <p className="text-[10px] text-neutral-500 mt-1">
                            {relativeTime(msg.createdAt)}
                          </p>
                        </div>
                      );
                    })
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input bar */}
                <div className={`flex-shrink-0 px-4 py-3 border-t ${isDark ? 'border-neutral-800' : 'border-gray-200'}`}>
                  <div
                    className={`flex items-end gap-3 rounded-2xl px-4 py-2.5 border transition-shadow focus-within:shadow-md ${
                      isDark
                        ? 'bg-neutral-800 border-neutral-700/60'
                        : 'bg-white border-gray-200/80'
                    }`}
                  >
                    <textarea
                      ref={textareaRef}
                      value={messageText}
                      onChange={(e) => setMessageText(e.target.value)}
                      onKeyDown={handleSupportKeyDown}
                      placeholder="Type a message..."
                      rows={1}
                      className={`flex-1 resize-none bg-transparent text-sm outline-none py-1 overflow-y-auto ${isDark ? 'text-neutral-100 placeholder-neutral-500' : 'text-gray-900 placeholder-gray-400'}`}
                      style={{ minHeight: '28px', maxHeight: '200px' }}
                    />
                    <button
                      onClick={sendMessage}
                      disabled={!messageText.trim() || sendingMessage}
                      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 disabled:opacity-30 disabled:scale-95"
                      style={{
                        backgroundColor: messageText.trim() && !sendingMessage ? '#8B5CF6' : (isDark ? '#3f3f46' : '#e5e7eb'),
                        color: messageText.trim() && !sendingMessage ? '#fff' : (isDark ? '#71717a' : '#9ca3af'),
                      }}
                    >
                      {sendingMessage ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ ANNOUNCE TAB ═══════════ */}
      {tab === 'announce' && (
        <div className="space-y-6 max-w-2xl">
          {/* Test / Prod toggle */}
          <div>
            <div className="flex gap-2 mb-2">
              <button
                onClick={() => setAnnounceMode('test')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  announceMode === 'test'
                    ? 'bg-amber-500 text-white'
                    : isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-500'
                }`}
              >
                Test
              </button>
              <button
                onClick={() => setAnnounceMode('prod')}
                className={`flex-1 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                  announceMode === 'prod'
                    ? 'bg-red-500 text-white'
                    : isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-500'
                }`}
              >
                Prod
              </button>
            </div>
            <p className={`text-xs ${textMuted}`}>
              {announceMode === 'test' ? 'Sends to your device only' : 'Sends to ALL users'}
            </p>
          </div>

          {/* Compose card */}
          <div className={`rounded-2xl p-5 ${isDark ? 'bg-neutral-900' : 'bg-white shadow-sm'}`} style={{
            border: `2px solid ${announceMode === 'test' ? '#F59E0B' : '#EF4444'}`,
          }}>
            <h3 className={`text-sm font-semibold mb-3 ${textSecondary}`}>Compose announcement</h3>
            <input
              type="text"
              placeholder="Title"
              value={announceTitle}
              onChange={e => setAnnounceTitle(e.target.value)}
              className={`w-full px-4 py-2 rounded-xl text-sm mb-3 outline-none ${isDark ? 'bg-neutral-800 text-white border border-neutral-700 placeholder-neutral-500' : 'bg-gray-50 text-black border border-gray-200 placeholder-gray-400'}`}
            />
            <textarea
              placeholder="Message..."
              value={announceMessage}
              onChange={e => setAnnounceMessage(e.target.value)}
              rows={4}
              className={`w-full px-4 py-2 rounded-xl text-sm mb-3 outline-none resize-none ${isDark ? 'bg-neutral-800 text-white border border-neutral-700 placeholder-neutral-500' : 'bg-gray-50 text-black border border-gray-200 placeholder-gray-400'}`}
            />
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  if (announceMode === 'prod') {
                    setShowProdConfirm(true);
                  } else {
                    handleSendAnnouncement(true);
                  }
                }}
                disabled={announceSending || !announceTitle.trim() || !announceMessage.trim()}
                className={`px-5 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-50 ${
                  announceMode === 'test' ? 'bg-amber-500 hover:bg-amber-600' : 'bg-red-500 hover:bg-red-600'
                }`}
              >
                {announceSending ? 'Sending...' : announceMode === 'test' ? 'Send test' : 'Send to all'}
              </button>
              {announceResult && (
                <span className={`text-sm ${textSecondary}`}>
                  Sent: {announceResult.sent} · Failed: {announceResult.failed} · Total: {announceResult.total}
                </span>
              )}
            </div>
          </div>

          {/* Prod confirmation dialog */}
          {showProdConfirm && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setShowProdConfirm(false)}>
              <div className={`${isDark ? 'bg-neutral-900 border-neutral-700' : 'bg-white border-gray-200'} border rounded-2xl w-full max-w-sm p-6 mx-4`} onClick={e => e.stopPropagation()}>
                <h3 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-black'}`}>Send to all users?</h3>
                <p className={`text-sm mb-5 ${textSecondary}`}>
                  This will send a push notification to <strong>every user</strong> with the app installed. This cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowProdConfirm(false)}
                    className={`flex-1 py-2.5 rounded-xl text-sm font-medium ${isDark ? 'bg-neutral-800 text-neutral-300' : 'bg-gray-100 text-gray-600'}`}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSendAnnouncement(false)}
                    disabled={announceSending}
                    className="flex-1 py-2.5 rounded-xl text-sm font-semibold bg-red-500 text-white disabled:opacity-50"
                  >
                    {announceSending ? 'Sending...' : 'Yes, send to all'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* History */}
          <div>
            <h3 className={`text-sm font-semibold mb-3 ${textSecondary}`}>History ({announcements.length})</h3>
            {announcements.length === 0 ? (
              <p className={`text-sm ${textMuted}`}>No announcements sent yet</p>
            ) : (
              <div className="space-y-2">
                {announcements.map(ann => (
                  <div
                    key={ann.id}
                    className={`rounded-xl p-4 cursor-pointer transition-colors ${isDark ? 'bg-neutral-900 border border-neutral-800 hover:border-neutral-700' : 'bg-white border border-gray-200 shadow-sm hover:border-gray-300'}`}
                    onClick={() => {
                      setAnnounceTitle(ann.title || '');
                      setAnnounceMessage(ann.message || '');
                    }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                            ann.testOnly
                              ? 'bg-amber-500/20 text-amber-500'
                              : 'bg-red-500/20 text-red-500'
                          }`}>
                            {ann.testOnly ? 'TEST' : 'PROD'}
                          </span>
                          <p className={`text-sm font-medium truncate ${isDark ? 'text-white' : 'text-black'}`}>{ann.title}</p>
                        </div>
                        <p className={`text-xs ${textSecondary} line-clamp-2`}>{ann.message}</p>
                        <p className={`text-[10px] mt-1 ${textMuted}`}>
                          {ann.createdAt?.toDate ? ann.createdAt.toDate().toLocaleString() : ann.createdAt?.seconds ? new Date(ann.createdAt.seconds * 1000).toLocaleString() : '—'}
                        </p>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAnnouncementDelete(ann.id);
                        }}
                        className={`p-1.5 rounded-lg flex-shrink-0 transition-colors ${isDark ? 'hover:bg-neutral-800 text-neutral-500 hover:text-red-400' : 'hover:bg-gray-100 text-gray-400 hover:text-red-500'}`}
                        title="Delete announcement"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════════ USER DETAIL / SUBSCRIPTION MODAL ═══════════ */}
      {selectedUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={() => setSelectedUser(null)}>
          <div className={`${isDark ? 'bg-neutral-900 border-neutral-700' : 'bg-white border-gray-200'} border rounded-2xl w-full max-w-lg p-6 mx-4`} onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h2 className={`text-lg font-bold ${textPrimary}`}>User detail</h2>
              <button onClick={() => setSelectedUser(null)} className={`p-1 rounded-lg ${isDark ? 'hover:bg-neutral-800' : 'hover:bg-gray-100'}`}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            {/* User info */}
            <div className="space-y-3 mb-6">
              <div className="flex justify-between">
                <span className={textSecondary}>Name</span>
                <span className={`font-medium ${textPrimary}`}>{selectedUser.displayName || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className={textSecondary}>Email</span>
                <span className={`font-medium ${textPrimary}`}>{selectedUser.email}</span>
              </div>
              <div className="flex justify-between">
                <span className={textSecondary}>Joined</span>
                <span className={textPrimary}>{formatDate(selectedUser.accountCreatedAt)}</span>
              </div>
              <div className="flex justify-between">
                <span className={textSecondary}>Last Active</span>
                <span className={textPrimary}>{daysAgo(selectedUser.lastActiveAt) || 'Never'}</span>
              </div>
              <div className="flex justify-between">
                <span className={textSecondary}>Device</span>
                <span className={textPrimary}>{selectedUser.deviceType === 'ios' ? 'iOS' : selectedUser.deviceType === 'android' ? 'Android' : '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className={textSecondary}>App version</span>
                <span className={textPrimary}>{selectedUser.appVersion || '—'}</span>
              </div>

              {/* Feature counts */}
              <div className={`pt-3 border-t ${isDark ? 'border-neutral-800' : 'border-gray-100'}`}>
                <p className={`text-xs font-semibold mb-2 ${textSecondary}`}>Feature usage</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { l: 'Tasks', v: selectedUser.taskCount },
                    { l: 'Habits', v: selectedUser.habitCount },
                    { l: 'Lists', v: selectedUser.listCount },
                    { l: 'Notes', v: selectedUser.noteCount },
                    { l: 'Moods', v: selectedUser.feelingCount },
                    { l: 'Affirmations', v: selectedUser.affirmationCount },
                    { l: 'Gantt', v: selectedUser.ganttCount },
                    { l: 'Budget', v: selectedUser.budgetCategoryCount },
                    { l: 'Connections', v: selectedUser.connectionCount },
                  ].map(f => (
                    <div key={f.l} className={`text-center p-2 rounded-lg ${isDark ? 'bg-neutral-800' : 'bg-gray-50'}`}>
                      <p className={`text-lg font-bold ${textPrimary}`}>{f.v}</p>
                      <p className={`text-[10px] ${textMuted}`}>{f.l}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Subscription management */}
            <div className={`pt-4 border-t ${isDark ? 'border-neutral-800' : 'border-gray-100'}`}>
              <p className={`text-xs font-semibold mb-3 ${textSecondary}`}>Subscription management</p>

              <div className="flex gap-2 mb-3">
                {['free', 'pro'].map(t => (
                  <button
                    key={t}
                    onClick={() => setModalTier(t)}
                    className={`flex-1 py-2 rounded-xl text-sm font-semibold capitalize ${
                      modalTier === t
                        ? t === 'pro' ? 'bg-purple-600 text-white' : isDark ? 'bg-white text-black' : 'bg-black text-white'
                        : isDark ? 'bg-neutral-800 text-neutral-400' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>

              {modalTier === 'pro' && (
                <div className="space-y-3 mb-3">
                  <div>
                    <label className={`text-xs ${textSecondary}`}>Expiration Date</label>
                    <input
                      type="date"
                      value={modalExpiry}
                      onChange={e => setModalExpiry(e.target.value)}
                      className={`w-full px-3 py-2 rounded-xl text-sm mt-1 outline-none ${isDark ? 'bg-neutral-800 text-white border border-neutral-700' : 'bg-gray-50 text-black border border-gray-200'}`}
                    />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={modalTrial}
                      onChange={e => setModalTrial(e.target.checked)}
                      className="rounded"
                    />
                    <span className={`text-sm ${textPrimary}`}>Trial period</span>
                  </label>
                </div>
              )}

              <button
                onClick={saveSubscription}
                disabled={modalSaving}
                className="w-full py-2.5 rounded-xl bg-purple-600 text-white text-sm font-semibold disabled:opacity-50"
              >
                {modalSaving ? 'Saving...' : 'Save Changes'}
              </button>

              <button
                onClick={async () => {
                  const uid = selectedUser!.uid;
                  const chatId = `support_${uid}`;
                  // Create chat doc if needed
                  const chatRef = doc(db, 'supportChats', chatId);
                  const { getDoc: getDocFn } = await import('firebase/firestore');
                  const existing = await getDocFn(chatRef);
                  if (!existing.exists()) {
                    // Find admin uid
                    const adminUid = auth.currentUser?.uid || '';
                    await setDoc(chatRef, {
                      userId: uid,
                      userEmail: selectedUser!.email,
                      userName: selectedUser!.displayName,
                      adminUid,
                      participants: [uid, adminUid],
                      lastMessage: '',
                      lastMessageAt: serverTimestamp(),
                      unreadByAdmin: 0,
                      unreadByUser: 0,
                      createdAt: serverTimestamp(),
                    });
                  }
                  setSelectedUser(null);
                  setTab('support');
                  // Select the chat after a brief delay to let the subscription pick it up
                  setTimeout(() => {
                    setSelectedChat({ id: chatId, userId: uid, userName: selectedUser!.displayName, userEmail: selectedUser!.email });
                  }, 500);
                }}
                className="w-full py-2 text-blue-500 text-sm font-medium hover:text-blue-400 transition-colors"
              >
                Message user
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────────
   KPI Card Component
   ──────────────────────────────────────────── */

const KPICard: React.FC<{
  isDark: boolean;
  label: string;
  value: number | string;
  accent?: 'purple' | 'green' | 'blue' | 'red';
}> = ({ isDark, label, value, accent }) => {
  const accentMap: Record<string, string> = {
    purple: 'text-purple-500',
    green: 'text-green-500',
    blue: 'text-blue-500',
    red: 'text-red-500',
  };
  const accentColor = (accent && accentMap[accent]) || (isDark ? 'text-white' : 'text-black');

  return (
    <div className={`rounded-2xl p-5 ${isDark ? 'bg-neutral-900 border border-neutral-800' : 'bg-white border border-gray-200 shadow-sm'}`}>
      <p className={`text-2xl font-bold ${accentColor}`}>{typeof value === 'number' ? value.toLocaleString() : value}</p>
      <p className={`text-xs mt-1 ${isDark ? 'text-neutral-500' : 'text-gray-400'}`}>{label}</p>
    </div>
  );
};

export default Admin;
